/*
 *
 *  MediaDispatcher
 *
 *  Notifies of media breakpoint changes (media query). Only works in conjunction with CSS.
 *  Example CSS:

	body:after {
		display: none;
		content: 'desktop';
		@include tablet {
			content: 'tablet';
		}
		@include tablet-portrait {
			content: 'tablet-portrait';
		}
		@include mobile {
			content: 'mobile';
		}
		@include mobile-portrait {
			content: 'mobile-portrait';
		}
	}

 *
 */

import fastdom from 'fastdom';
import DeferredBoolean from '../../lib/util/DeferredBoolean.js';
/**
 * Usage:
 *
 * mediaTrigger.is('mobile').then(function(){ ... });
 * -- triggered only when mobile
 * mediaTrigger.isNot('tablet').then(function(){ ... });
 * -- triggered when not tablet, so for tablet-portrait, mobile, mobile-portrait or default
 * mediaTrigger.isOrBelow('tablet-portrait').then(function(){ ... });
 * -- triggered when tablet-portrait, mobile, mobile-portrait
 * mediaTrigger.isOrAbove('tablet').then(function(){ ... });
 * -- triggered when tablet or default (desktop)
 * mediaTrigger.isBelow('tablet-portrait').then(function(){ ... });
 * -- triggered when mobile, mobile-portrait
 * mediaTrigger.isAbove('mobile').then(function(){ ... });
 * -- triggered when tablet-portrait, tablet, default (desktop)
 * mediaTrigger.isNone().then(function(){ ... });
 * mediaTrigger.isDefault().then(function(){ ... });
 * -- triggered when default (desktop)
 *
 * Alternative syntax:
 * mediaTrigger.is('mobile', function(){ ... });
 *
 */

var $window = $(window);
var triggers = [];
var falseTrigger = new DeferredBoolean(false);

function registerMediaTrigger(value) {
    triggers.push(value);
    value.validate();
}

function handleResize() {
    for (var i = triggers.length - 1; i >= 0; i--) {
        triggers[i].validate();
    }
}
$window.on('resize', () => handleResize());
handleResize();

function Trigger(name, index) {
    this.name = name;
    this.index = index;
    this.isOrBelow = new DeferredBoolean();
    this.isOrAbove = new DeferredBoolean();
    this.isExactly = new DeferredBoolean();
}

var MediaTrigger = function (triggerPoints) {
    this.element = document.body;
    this.pseudo = 'after';
    this.mediaIndex = -1;
    this.triggerPoints = [];
    if (triggerPoints) {
        this.setTriggerPoints(triggerPoints);
    }
    registerMediaTrigger(this);
};

MediaTrigger.prototype.setTriggerPoints = function (value) {
    this.triggerPoints = [];
    this.triggerPoints.push(new Trigger('default', 0));
    for (var i = 0; i < value.length; i++) {
        this.triggerPoints.push(new Trigger(value[i], this.triggerPoints.length));
    }
};

MediaTrigger.prototype.is = function (media, callback) {
    return this._is(media, 0, 'isExactly', callback);
};

MediaTrigger.prototype.isOrAbove = function (media, callback) {
    return this._is(media, 0, 'isOrAbove', callback);
};

MediaTrigger.prototype.isOrBelow = function (media, callback) {
    return this._is(media, 0, 'isOrBelow', callback);
};

MediaTrigger.prototype.isAbove = function (media, callback) {
    return this._is(media, -1, 'isOrAbove', callback);
};

MediaTrigger.prototype.isBelow = function (media, callback) {
    return this._is(media, 1, 'isOrBelow', callback);
};

MediaTrigger.prototype._is = function (media, indexDiff, triggerId, callback) {
    var triggerpoint = this._getTrigger(media);
    if (!triggerpoint) {
        throw new Error("Media Trigger point '" + media + "' doesn't exist.");
    }
    if (indexDiff) {
        triggerpoint = this.triggerPoints[triggerpoint.index + indexDiff];
    }
    var trigger = triggerpoint ? triggerpoint[triggerId] : falseTrigger;
    if (callback) {
        trigger.then(callback);
    }
    return trigger;
};

MediaTrigger.prototype._getTrigger = function (media) {
    for (var i = this.triggerPoints.length - 1; i >= 0; i--) {
        if (this.triggerPoints[i].name === media) {
            return this.triggerPoints[i];
        }
    }
    return null;
};

MediaTrigger.prototype._setMediaIndex = function (value) {
    var i,
        trigger,
        current = this.mediaIndex;
    if (value !== current) {
        // console.log("Media is: " + this.triggerPoints[value].name + '/' + value); // *remove*
        this.mediaIndex = value;
        for (i = this.triggerPoints.length - 1; i >= 0; i--) {
            trigger = this.triggerPoints[i];
            trigger.isOrBelow.set(i <= this.mediaIndex);
            trigger.isExactly.set(i === this.mediaIndex);
            trigger.isOrAbove.set(i >= this.mediaIndex);
        }
    }
};

MediaTrigger.prototype._setMedia = function (value) {
    for (var i = this.triggerPoints.length - 1; i > 0; i--) {
        if (this.triggerPoints[i].name === value) {
            this._setMediaIndex(i);
            return;
        }
    }
    this._setMediaIndex(0);
};

MediaTrigger.prototype.validate = function () {
    var cssValue;
    fastdom.measure(
        function measureTriggerPoint() {
            if (window.getComputedStyle) {
                cssValue = window
                    .getComputedStyle(this.element, ':' + this.pseudo)
                    .getPropertyValue('content')
                    .replace(/['"]+/g, '');
            }
            if (!cssValue) {
                cssValue = this.getFallback();
            }
        }.bind(this),
    );
    fastdom.mutate(
        function setTriggerPoint() {
            this._setMedia(cssValue);
        }.bind(this),
    );
};

MediaTrigger.prototype.unlisten = function (callback, target) {
    for (var i = this.triggerPoints.length - 1; i >= 0; i--) {
        var p = this.triggerPoints[i];
        p.isExactly.unlisten(callback, target);
        p.isOrAbove.unlisten(callback, target);
        p.isOrBelow.unlisten(callback, target);
    }
};

MediaTrigger.prototype.getFallback = function () {
    if (!this.$fallback) {
        this.$fallback = $('<div class="media-dispatcher-fallback"></div>');
        this.$fallback.css({ position: 'absolute', left: -1000 });
        this.$fallback.appendTo('body');

        // console.log('Created MediaTrigger Fallback'); // *remove*
    }
    var index = parseInt(this.$fallback.css('margin-bottom'), 10);
    var point = this.triggerPoints[index];
    return point ? point.name : 'unknown';
};

export default MediaTrigger;
